import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { MapPin, Navigation, Clock } from "lucide-react";
import { Capacitor } from "@capacitor/core";

/**
 * ParkingCard — shows "🅿️ Parking Saved" with address, timestamp,
 * and "Open in Maps" deep-link button. Auto-expires after 24h.
 * Falls back to Capacitor Preferences if backend has no recent save.
 */
export default function ParkingCard() {
  const [parking, setParking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadParking();
  }, []);

  const loadParking = async () => {
    try {
      const res = await api.get("/mileage/parking/latest");
      if (res.data?.parking) {
        setParking(res.data.parking);
      } else {
        // Fallback: check Capacitor Preferences
        try {
          const { Preferences } = await import("@capacitor/preferences");
          const { value } = await Preferences.get({ key: "sq_last_parking" });
          if (value) {
            const parsed = JSON.parse(value);
            // Check if it's less than 24h old
            const savedAt = new Date(parsed.saved_at).getTime();
            if (Date.now() - savedAt < 24 * 60 * 60 * 1000) {
              setParking(parsed);
            }
          }
        } catch (_) { /* no fallback available */ }
      }
    } catch (e) {
      console.warn("[ParkingCard] Failed to load parking:", e);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !parking) return null;

  // Check expiry (24h)
  const savedAt = new Date(parking.saved_at);
  if (Date.now() - savedAt.getTime() > 24 * 60 * 60 * 1000) return null;

  const timeAgo = getTimeAgo(savedAt);

  const openInMaps = () => {
    const { lat, lng } = parking;
    // Platform-appropriate deep links
    if (Capacitor.getPlatform() === "ios") {
      window.open(`maps://maps.apple.com/?ll=${lat},${lng}&q=My+Car`, "_system");
    } else {
      window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, "_system");
    }
  };

  return (
    <div className="border border-[#007AFF]/30 bg-[#007AFF]/8 p-4" data-testid="parking-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="text-2xl">🅿️</div>
          <div>
            <div className="font-display text-sm text-[#007AFF] tracking-wide">PARKING SAVED</div>
            {parking.address ? (
              <p className="text-sm text-zinc-300 mt-1">{parking.address}</p>
            ) : (
              <p className="text-xs text-zinc-500 mt-1">
                {parking.lat.toFixed(5)}, {parking.lng.toFixed(5)}
              </p>
            )}
            <div className="flex items-center gap-1.5 mt-1.5 text-xs text-zinc-500">
              <Clock className="w-3 h-3" />
              <span>{timeAgo}</span>
            </div>
          </div>
        </div>
        <button
          onClick={openInMaps}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-[#007AFF]/15 text-[#007AFF] border border-[#007AFF]/30 hover:bg-[#007AFF]/25 transition-colors"
          style={{ touchAction: "manipulation" }}
          data-testid="parking-open-maps"
        >
          <Navigation className="w-3.5 h-3.5" />
          Maps
        </button>
      </div>
    </div>
  );
}

function getTimeAgo(date) {
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return "Over 24h ago";
}

/**
 * ManualParkingSaveButton — for users not using Auto-Track.
 * Grabs current position and POSTs to /mileage/parking.
 */
export function ManualParkingSaveButton({ onSaved }) {
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const pos = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
        });
      });
      const payload = {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        address: null,
      };
      await api.post("/mileage/parking", payload);
      // Also save to Preferences as fallback
      try {
        const { Preferences } = await import("@capacitor/preferences");
        await Preferences.set({
          key: "sq_last_parking",
          value: JSON.stringify({ ...payload, saved_at: new Date().toISOString() }),
        });
      } catch (_) {}
      if (onSaved) onSaved();
    } catch (e) {
      console.error("[ParkingCard] Manual save failed:", e);
      alert("Could not get your location. Make sure location permissions are enabled.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <button
      onClick={save}
      disabled={saving}
      className="flex items-center gap-2 px-4 py-2.5 text-sm border border-white/10 text-zinc-300 hover:text-white hover:border-white/20 transition-colors disabled:opacity-50"
      style={{ touchAction: "manipulation" }}
      data-testid="manual-parking-save-btn"
    >
      <MapPin className="w-4 h-4" />
      {saving ? "Saving…" : "Save Parking"}
    </button>
  );
}
